import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../components/forms/FormInput';
import PasswordInput from '../../components/forms/PasswordInput';
import SelectInput from '../../components/forms/SelectInput';
import Button from '../../components/common/Button';
import { useToast } from '../../components/common/Toast';
import { sellerService } from '../../services/seller.service';
import { useForm } from '../../hooks/useForm';
import { validators } from '../../utils/validators';
import { BUSINESS_TYPES } from '../../config/constants';
import { theme } from '../../config/theme';

const SellerRegister = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const { values, errors, touched, handleChange, handleBlur, validate, setValues } = useForm(
    {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirm_password: '',
      store_name: '',
      business_type: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
    },
    {
      first_name: (value) => currentStep === 1 ? validators.name(value, 'First name') : null,
      last_name: (value) => currentStep === 1 ? validators.name(value, 'Last name') : null,
      email: (value) => currentStep === 1 ? validators.email(value) : null,
      phone: (value) => currentStep === 1 ? validators.phone(value, true) : null,
      password: (value) => currentStep === 1 ? validators.password(value) : null,
      confirm_password: (value, all) => currentStep === 1 ? validators.confirmPassword(value, all.password) : null,
      store_name: (value) => currentStep === 2 ? validators.required(value, 'Store name') : null,
      business_type: (value) => currentStep === 2 ? validators.required(value, 'Business type') : null,
      address_line1: (value) => currentStep === 3 ? validators.required(value, 'Address') : null,
      city: (value) => currentStep === 3 ? validators.required(value, 'City') : null,
      state: (value) => currentStep === 3 ? validators.required(value, 'State') : null,
      pincode: (value) => currentStep === 3 ? validators.pincode(value) : null,
    }
  );

  const handleNext = () => {
    if (validate()) setCurrentStep(prev => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const { confirm_password, ...registerData } = values;
      const response = await sellerService.register(registerData);
      
      sessionStorage.setItem('seller_temp_data', JSON.stringify({
        phone: values.phone,
        email: values.email,
        otpExpiresAt: response.data.otpExpiresAt,
      }));
      
      toast.success('OTP sent to your phone number!');
      navigate('/seller/verify-otp');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const titleStyles = {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
  };

  const stepperStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing.md,
    marginBottom: theme.spacing['2xl'],
  };

  const stepDotStyles = (active) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: active ? theme.colors.primary.main : theme.colors.gray[300],
    transition: theme.transitions.base,
  });

  const rowStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.md,
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 style={titleStyles}>Create Seller Account</h2>

      <div style={stepperStyles}>
        {[1, 2, 3].map(step => (
          <div key={step} style={stepDotStyles(currentStep >= step)} />
        ))}
      </div>

      {currentStep === 1 && (
        <>
          <div style={rowStyles}>
            <FormInput label="First Name" name="first_name" value={values.first_name} onChange={handleChange} onBlur={handleBlur} error={errors.first_name} touched={touched.first_name} required />
            <FormInput label="Last Name" name="last_name" value={values.last_name} onChange={handleChange} onBlur={handleBlur} error={errors.last_name} touched={touched.last_name} required />
          </div>
          <FormInput label="Email" name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={errors.email} touched={touched.email} required />
          <FormInput label="Phone" name="phone" type="tel" value={values.phone} onChange={handleChange} onBlur={handleBlur} placeholder="9876543210" error={errors.phone} touched={touched.phone} required />
          <PasswordInput label="Password" name="password" value={values.password} onChange={handleChange} onBlur={handleBlur} error={errors.password} touched={touched.password} required />
          <PasswordInput label="Confirm Password" name="confirm_password" value={values.confirm_password} onChange={handleChange} onBlur={handleBlur} error={errors.confirm_password} touched={touched.confirm_password} required />
        </>
      )}

      {currentStep === 2 && (
        <>
          <FormInput label="Store Name" name="store_name" value={values.store_name} onChange={handleChange} onBlur={handleBlur} placeholder="Jane's Electronics" error={errors.store_name} touched={touched.store_name} required />
          <SelectInput label="Business Type" name="business_type" value={values.business_type} onChange={handleChange} onBlur={handleBlur} options={BUSINESS_TYPES} error={errors.business_type} touched={touched.business_type} required />
        </>
      )}

      {currentStep === 3 && (
        <>
          <FormInput label="Address Line 1" name="address_line1" value={values.address_line1} onChange={handleChange} onBlur={handleBlur} placeholder="Shop No. 123, Main Street" error={errors.address_line1} touched={touched.address_line1} required />
          <FormInput label="Address Line 2" name="address_line2" value={values.address_line2} onChange={handleChange} onBlur={handleBlur} placeholder="Near City Mall" />
          <div style={rowStyles}>
            <FormInput label="City" name="city" value={values.city} onChange={handleChange} onBlur={handleBlur} error={errors.city} touched={touched.city} required />
            <FormInput label="State" name="state" value={values.state} onChange={handleChange} onBlur={handleBlur} error={errors.state} touched={touched.state} required />
          </div>
          <FormInput label="Pincode" name="pincode" value={values.pincode} onChange={handleChange} onBlur={handleBlur} placeholder="400001" error={errors.pincode} touched={touched.pincode} required />
        </>
      )}

      <div style={{ display: 'flex', justifyContent: currentStep > 1 ? 'space-between' : 'flex-end', marginTop: theme.spacing.xl }}>
        {currentStep > 1 && (
          <Button 
            type="button" 
            onClick={() => setCurrentStep(prev => prev - 1)} 
            variant="secondary"
          >
            Previous
          </Button>
        )}
        {currentStep < 3 ? (
          <Button 
            type="button" 
            onClick={handleNext}
          >
            Next
          </Button>
        ) : (
          <Button 
            type="submit" 
            loading={loading}
          >
            Register & Send OTP
          </Button>
        )}
      </div>
    </form>
  );
};

export default SellerRegister;